import React from 'react'
import { inject, observer } from 'mobx-react'
import { isNil, isNull, sortBy } from 'lodash'
import moment from 'moment'
import { t } from 'i18next'
import Grid from '@material-ui/core/Grid'
import Button from '@material-ui/core/Button'
import { TextField } from '@crystalservice/crystals-ui/lib/components/inputs/text-field/text-field'
import { MaskedInput } from '@crystalservice/crystals-ui/lib/components/inputs/masked-input/masked-input'
import { SelectInput, DefaultSelectOption } from '@crystalservice/crystals-ui/lib/components/inputs/select-input/select-input'
import { Checkbox } from '@crystalservice/crystals-ui/lib/components/inputs/checkbox/checkbox'
import { DatePicker } from '@crystalservice/crystals-ui/lib/components/date-and-time-pickers/date-picker/date-picker'
import { NumberInput } from '@crystalservice/crystals-ui/lib/components/inputs/number-input/number-input'
import { IntlPhoneInput } from './intl-phone-input'
import { Timer } from './timer'
import { SetCaptcha } from './set-captcha'
import { AnimalSelection } from './form-components/animal-selection'
import { ChildrenSelection } from './form-components/children-selection'

import { RegistrationStore } from '../../stores/registration-store'
import {
    CARDS_MASK,
    CARDS_MASK_FORMAT,
    cardNumberToServerValidValue,
    cardNumberToFrontFormattedValue,
    phoneToFrontFormattedValue,
} from '../../domain/client-masks'
import { CompletedField, Field, Selection } from '../../domain/form'
import { components } from '../../domain/entities'
import { animalsValuesCleaner, isChildrenListEmpty } from './form-components/utils'
import { ValidationResult } from '../../utils/form-validation/form-validation'
import {
    nameValidator,
    emailValidator,
    childrenNameValidator,
    regExpValidator,
} from '../../utils/form-validation/validators/regexp-validator'
import { dateValidator } from '../../utils/form-validation/validators/date-validator'
import { childDateValidator } from '../../utils/form-validation/validators/child-date-validator'
import { convertToNameFormat } from '../../utils/string-utils'
import { CountryCodeISO, getCountryCodeByPhone, intlPhoneSettings } from '../../utils/phone-utils'

const styles = require('./registration-page.module.scss')

const onlyDigits: RegExp = new RegExp(/^\d*$/)
const MAX_PHONE_CHECKS = 3

export const NOT_SELECTED_VALUE = '_notSelected_'
const getSelectionOpts = (opts: Selection[]): DefaultSelectOption[] => {
    return [
        {
            label: 'Не заполнено',
            value: NOT_SELECTED_VALUE,
        },
        ...opts.map(opt => ({
            value: opt.id,
            label: opt.localised,
        }))
    ]
}

const getClearSelectionOpts = (opts: Selection[]): DefaultSelectOption[] => {
    return opts.map(opt => ({
        value: opt.id,
        label: opt.localised,
    }))
}

const isFieldValid = (fieldId: string, value: any): ValidationResult => {
    switch (fieldId) {
        case 'first_name':
        case 'last_name':
            return nameValidator(value)
        case 'email':
            return emailValidator(value)
        case 'phone':
            if (!value) {
                return { valid: true }
            }

            const countryCode = getCountryCodeByPhone(value)
            const phoneRegExp = intlPhoneSettings[countryCode].validationRegExp
            const validator = regExpValidator(phoneRegExp, 'Неверный формат номера телефона')

            return validator(value)
        case 'birthday':
            return dateValidator(value)
        case 'children':
            return !isChildrenListEmpty(value) ? { valid: childrenNameValidator(value).valid && childDateValidator(value).valid }
                : { valid: true }
        default:
            return { valid: true }
    }
}

// Проверяем на null и пустую строку, т.к. число 0 = валидное значение
const isFieldEmpty = (value: any): boolean => {
    return isNil(value) || value === ''
}

interface RegistrationFormProps {
    registrationStore?: RegistrationStore
}

interface RegistrationFormState {
    verifyCode: string
    originalPhone: string
    codeCheckedTimes: number
    canResend: boolean
    cardCheckedTimes: number
    agreement: boolean
    captcha: boolean
    isFormValid: boolean
    formAttributes: {
        [attribute: string]: any
    }
    /**
     * Выбранный код страны при указании номера телефона.
     * Будет выставлен только если пользователь явно выбрал код страны в селекте с телефоном.
     * Используется чтобы сохранить выбранную страну на странице подтвержения телефона.
     */
    phoneCountryCode: CountryCodeISO
}

@inject(RegistrationStore.storeName)
@observer
export class RegistrationForm extends React.Component<RegistrationFormProps, RegistrationFormState> {
    state: RegistrationFormState = {
        verifyCode: '',
        originalPhone: '',
        codeCheckedTimes: 0,
        canResend: false, /* в будущем возможно захочется переотправлять код, если ошиблись телефоном, поэтому не удалять */
        cardCheckedTimes: 0,
        agreement: false,
        captcha: false,
        isFormValid: true,
        formAttributes: {},
        phoneCountryCode: null,
    }

    componentDidMount() {
        const { form } = this.props.registrationStore

        let defaultAttributes = null
        form.fields.forEach(item => {
            if (item.required && item.type === 'BOOL') {
                if (!defaultAttributes) {
                    defaultAttributes = {}
                }
                defaultAttributes[item.id] = false
            }
        })
        if (defaultAttributes) {
            this.setState({
                formAttributes: defaultAttributes
            })
        }
    }

    get isAllRequiredFieldsFilled(): boolean {
        const { agreement, captcha } = this.state
        const { registrationStore } = this.props
        const { form } = registrationStore
        const requiredFields = form?.fields?.filter(field => {
            // у BOOL нельзя ставить обязательно, т.к. false тоже валидное значение
            if (field.type === 'BOOL') return false
            return field.required
        })
        let isAllRequiredFieldsFilled: boolean = true

        requiredFields.forEach(field => {
            if (isFieldEmpty(this.state.formAttributes[field.id])) {
                isAllRequiredFieldsFilled = false
            }
        })

        if (!agreement) {
            isAllRequiredFieldsFilled = false
        }

        if (!captcha) {
            isAllRequiredFieldsFilled = false
        }

        return isAllRequiredFieldsFilled && this.isFieldsValid()
    }

    get isAllFieldsEmpty(): boolean {
        const { form } = this.props.registrationStore
        return form?.fields?.every(field => isFieldEmpty(this.state.formAttributes[field.id]))
    }

    get filledPrecentage(): number {
        const { form } = this.props.registrationStore
        const { agreement, captcha, formAttributes } = this.state

        const requiredFields = form?.fields?.filter(field => {
            // у BOOL нельзя ставить обязательно, т.к. false тоже валидное значение
            if (field.type === 'BOOL') return false
            return field.required
        })
        // + 1 - согласие на обработку
        // + 1 - заполненный ответ на капчу
        const allFieldsLength: number = requiredFields?.length + 2
        let filledFieldsLength: number = 0

        if (agreement) filledFieldsLength += 1
        if (captcha) filledFieldsLength += 1

        requiredFields?.forEach(field => {
            if (!isFieldEmpty(formAttributes[field.id]) && isFieldValid(field.id, formAttributes[field.id]).valid) filledFieldsLength += 1
        })

        // Если все поля пустые и нет обязательных полей, даже при включенной галочке agreement показываем ноль заполнения
        if (!requiredFields?.length && this.isAllFieldsEmpty) {
            return 0
        }

        return Math.round(filledFieldsLength / allFieldsLength * 100)
    }

    get formFields(): CompletedField[] {
        const { form } = this.props.registrationStore
        const { formAttributes } = this.state
        const formFields: CompletedField[] = []

        form.fields?.forEach(field => {
            if (!isFieldEmpty(formAttributes[field.id])) {
                if (typeof formAttributes[field.id] === 'string') {
                    formAttributes[field.id]?.trim()
                }

                if (
                    field.id === 'children'
                    && isChildrenListEmpty(formAttributes[field?.id])
                    && formAttributes[field?.id]?.length > 0
                ) {
                    formAttributes[field.id][0].name = null
                }

                const notStringValue = field.id === 'children'
                    ? JSON.stringify(formAttributes[field.id])
                    : formAttributes[field.id]

                formFields.push({
                    id: field.id,
                    value: typeof formAttributes[field.id] === 'string'
                        ? formAttributes[field.id]?.trim()
                        : notStringValue
                })
            }
        })

        return formFields
    }

    isFieldsValid = (): boolean => {
        const { formAttributes } = this.state
        const filledFields = Object.keys(formAttributes)
        let isFormFilledCorrectly: boolean = true

        filledFields.forEach(field => {
            if (!isFieldValid(field, formAttributes[field]).valid) isFormFilledCorrectly = false
        })

        return isFormFilledCorrectly
    }

    getFieldErrorById = (id: string): string => {
        const { formAttributes } = this.state
        switch (id) {
            case 'first_name':
            case 'last_name':
                return nameValidator(formAttributes[id]).error || ''
            case 'email':
                return emailValidator(formAttributes[id]).error || ''
            case 'phone':
                if (!formAttributes[id]) {
                    return ''
                }

                const countryCode = getCountryCodeByPhone(formAttributes[id])
                const phoneRegExp = intlPhoneSettings[countryCode].validationRegExp
                const validator = regExpValidator(phoneRegExp, 'Неверный формат номера телефона')

                return validator(formAttributes[id]).error || ''
            case 'children':
                return childrenNameValidator(formAttributes[id]).error || ''
            default:
                return ''
        }
    }

    getChildrenInfo = (children): React.ReactNode => {
        const getGenderLabel = (gender: 'MALE' | 'FEMALE' | 'UNKNOWN'): string => {
            if (gender === 'MALE') {
                return 'Мальчик'
            }

            if (gender === 'FEMALE') {
                return 'Девочка'
            }

            return null
        }

        const getBirthDateLabel = (birthDate: Date | string): string => {
            if (isNil(birthDate)) {
                return null
            }

            return moment(birthDate, 'YYYY-MM-DD').format('DD.MM.YYYY')
        }

        if (isNil(children)) {
            return '—'
        }

        if (
            children.length === 1 &&
            isNull(children[0].name) &&
            isNull(children[0].birthDate) &&
            isNull(children[0].gender)
        ) {
            return 'Есть'
        }

        if (children.length === 0) {
            return 'Нет'
        }

        return (
            <>
                {children.map(({ name, birthDate, gender }) => {
                    const childInfo = [
                        getGenderLabel(gender),
                        name,
                        getBirthDateLabel(birthDate),
                    ].filter(Boolean).join(' ')

                    return <p>{childInfo}</p>
                })}
            </>
        )
    }

    getReadOnlyField = (field: Field): React.ReactNode => {
        const { formAttributes } = this.state
        const { form } = this.props.registrationStore

        const label = (
            <p id={`fieldLabel-${field.id}`} className={styles.label}>
                {field.text}
                {field.required ? (
                    <span className={styles.required}>{' (обязательно)'}</span>
                ) : ''}
            </p>
        )

        let fieldValueString: string = formAttributes[field.id] || '—';

        switch (field.type) {
            case 'BOOL':
                return (
                    <Checkbox
                        label={field.text}
                        disabled
                        checked={formAttributes[field.id]}
                    />
                )
            case 'SELECTION':
                const value = formAttributes[field.id]
                const selectedOption = field.selection.find(item => item.id === value)
                fieldValueString = value ? selectedOption.localised : '—'
                break
            case 'DATE':
                const date = moment(formAttributes[field.id], 'YYYY-MM-DD')
                fieldValueString = formAttributes[field.id] ? date.format('DD.MM.YYYY') : '—'
                break
            case 'CARD':
                fieldValueString = formAttributes[field.id]
                    ? cardNumberToFrontFormattedValue(formAttributes[field.id]) || '—'
                    : '—'
                break
            case 'PHONE':
                fieldValueString = phoneToFrontFormattedValue(formAttributes[field.id]) || '—'
                break
            case 'CHILDREN':
                return (
                    <>
                        {label}
                        {this.getChildrenInfo(formAttributes[field.id])}
                    </>
                )
            case 'MULTISELECTION':
                const formField = form.fields.find(({ id }) => id === field.id)

                if (isNil(formAttributes[field.id]) || !formField) {
                    fieldValueString = '—'

                    break
                }

                if (formAttributes[field.id] === 'false') {
                    fieldValueString = 'Нет'

                    break
                }

                if (formAttributes[field.id] === 'true') {
                    fieldValueString = 'Есть'

                    break
                }

                fieldValueString = formAttributes[field.id]
                    .split(',')
                    .map(selectionId => formField.selection.find(
                        ({ id }) => id === selectionId
                    )?.localised || selectionId)
                    .join(', ')

                break
        }

        return (
            <>
                {label}
                <div id={`${field.id}-FieldValue`} className={styles.fieldValue}>
                    {fieldValueString}
                </div>
            </>
        )
    }

    setFieldValue = (value: any, id: string): void => {
        const { editError } = this.props.registrationStore
        if (Object.keys(editError).includes(id)) {
            delete editError[id]
            this.props.registrationStore.setEditError(editError)
        }
        this.setState(prevState => {
            return {
                formAttributes: {
                    ...prevState.formAttributes,
                    [id]: value
                },
                isFormValid: true
            }
        })
    }

    getFormFieldById = (field: Field, phones: CountryCodeISO[]): React.ReactNode => {
        const { formAttributes } = this.state
        const fieldValue = formAttributes[field.id]

        const now = moment().startOf('day')
        const birthday = this.formFields.find(field => field.id === 'birthday')?.value
        const birthdayMoment = moment(birthday)
        const ageInYears = now.diff(birthdayMoment, 'year')

        let showLabel = true
        let input

        switch (field.type) {
            case 'EMAIL':
                input = (
                    <TextField
                        id={`registrationField-${field.id}`}
                        className={styles.textField}
                        type="email"
                        placeholder={field.prompt}
                        required={field.required}
                        value={fieldValue || ''}
                        onValueChange={newValue => {
                            this.setFieldValue(newValue, field.id)
                        }}
                        errorText={this.getFieldErrorById(field.id)}
                    />
                )
                break
            case 'PHONE':
                input = (
                    <IntlPhoneInput
                        countries={phones}
                        value={fieldValue}
                        onValueChange={(newValue) => this.setFieldValue(newValue, field.id)}
                        onCountryChange={(countryCode) => this.setState({ phoneCountryCode: countryCode })}
                        error={false}
                        errorText={this.getFieldErrorById(field.id)}
                    />
                )
                break
            case 'DATE':
                input = (
                    <div id="birthdayDatePicker">
                        <Grid style={{ display: "flex" }}>
                            <Grid item xs={9} style={{ flexGrow: 1 }}>
                                <DatePicker
                                    id={`registrationField-${field.id}`}
                                    className={styles.dateField}
                                    minYear={1900}
                                    outOfRangeText="Некорректное значение"
                                    placeholder={field.prompt}
                                    required={field.required}
                                    withYearSelect
                                    defaultDate={null}
                                    disabledDateRanges={[
                                        {
                                            end: moment(0).set('year', 1900).startOf('year').toDate()
                                        },
                                        {
                                            start: moment().add(1, 'day').toDate()
                                        }
                                    ]}
                                    onInternalErrorChange={(error, date) => {
                                        this.setFieldValue(date ? moment(date).format('YYYY-MM-DD') : null, field.id)

                                        if (error) {
                                            this.setFieldValue(date ? moment(date).format('YYYY-MM-DD') : 'invalid', field.id)
                                        }
                                    }}
                                    onDateChange={date => {
                                        this.setFieldValue(date ? moment(date).format('YYYY-MM-DD') : null, field.id)
                                    }}
                                />
                            </Grid>
                                <Grid item xs={3}>
                                    {birthday && !isNaN(ageInYears) && (
                                        <div className={styles.ageLabel}>
                                            {
                                                ageInYears >= 1
                                                    ? t('registrationDetails.ageInYears.age', { count: ageInYears })
                                                    : t('registrationDetails.ageInYears.lessYear')
                                            }
                                        </div>
                                    )}
                                </Grid>
                        </Grid>
                    </div>
                )
                break
            case 'CARD':
                input = (
                    <MaskedInput
                        id={`registrationField-${field.id}`}
                        className={styles.textField}
                        variant="outlined"
                        inputMode="numeric"
                        placeholder={CARDS_MASK_FORMAT}
                        mask={CARDS_MASK}
                        autoComplete="off"
                        value={fieldValue}
                        onValueChange={newValue => {
                            this.setFieldValue(cardNumberToServerValidValue(newValue), field.id)
                        }}
                        onPaste={e => e.preventDefault()}
                    />
                )
                break
            case 'BOOL':
                showLabel = false
                input = (
                    <Checkbox
                        id={`registrationField-${field.id}`}
                        label={(
                            <>
                                <span>{field.text}</span>
                                {field.required && <span className={styles.required}>{' (обязательно)'}</span>}
                            </>
                        ) as any}
                        checked={Boolean(fieldValue)}
                        onValueChange={newValue => {
                            this.setFieldValue(newValue, field.id)
                        }}
                    />
                )
                break
            case 'SELECTION':
                let selectionOptions = field.selection

                if (field?.id === 'gender') {
                    selectionOptions = selectionOptions.filter(({ id }) => {
                       return id === 'male' || id === 'female'
                    })
                }

                input = (
                    <SelectInput
                        id={`registrationField-${field.id}`}
                        className={styles.select}
                        placeholder={field.prompt}
                        required={field.required}
                        value={fieldValue || NOT_SELECTED_VALUE}
                        options={getSelectionOpts(selectionOptions || [])}
                        onSelect={opt => {
                            if (opt.value === NOT_SELECTED_VALUE) {
                                this.setFieldValue(null, field.id)
                            } else {
                                this.setFieldValue(opt.value, field.id)
                            }
                        }}
                    />
                )
                break
            case 'NAME':
                input = (
                    <TextField
                        id={`registrationField-${field.id}`}
                        className={styles.textField}
                        placeholder={field.prompt}
                        required={field.required}
                        value={fieldValue || ''}
                        onValueChange={newValue => {
                            let value = newValue
                            // Для имени первую букву делаем заглавной
                            if (field.id === 'first_name' || field.id === 'middle_name' || field.id === 'last_name') {
                                value = convertToNameFormat(value)
                            }

                            this.setFieldValue(value, field.id)
                        }}
                        errorText={this.getFieldErrorById(field.id)}
                    />
                )
                break
            case 'NUMBER':
                input = (
                    <NumberInput
                        id={`registrationField-${field.id}`}
                        className={styles.textField}
                        placeholder={field.prompt}
                        required={field.required}
                        value={fieldValue}
                        onValueChange={newValue => {
                            this.setFieldValue(newValue, field.id)
                        }}
                        errorText={this.getFieldErrorById(field.id)}
                    />
                )
                break
            case 'MULTISELECTION':
                const filteredOptions = field.selection?.filter(x => animalsValuesCleaner(x.id))

                // для обратной совместимости
                input = field.id === 'animals' ?
                (
                    <AnimalSelection
                        id={field.id}
                        required={field.required}
                        value={fieldValue}
                        onValueChange={this.setFieldValue}
                        options={getClearSelectionOpts(filteredOptions || [])}
                    />
                ) : (
                    <SelectInput
                        id={field.id}
                        multiple
                        value={fieldValue?.split(',') || []}
                        options={(field.selection).map(it => ({
                            label: it.localised,
                            value: it.id,
                        }))}
                        onMultipleSelect={(selectedOptions) => {
                            this.setFieldValue(
                                selectedOptions?.map(selectedOption => selectedOption.value).join(','),
                                field.id
                            )
                        }}
                    />
                )
                break
            case 'CHILDREN':
                input = (
                    <ChildrenSelection
                        id={field.id}
                        required={field.required}
                        value={fieldValue}
                        onValueChange={this.setFieldValue}
                    />
                )
                break
        }
        return (
            <Grid key={field.id} item xs={12}>
                {showLabel && (
                    <p id={`fieldLabel-${field.id}`} className={styles.label}>
                        {field.text}
                        {field.required ? (
                            <span className={styles.required}>{' (обязательно)'}</span>
                        ) : ''}
                    </p>
                )}
                {input}
            </Grid>
        )
    }

    verificationTimerCallback = (): void => {
        const { setCodeObsolete } = this.props.registrationStore
        setCodeObsolete(true)
    }

    getVerificationPage = (): JSX.Element => {
        const {
            form,
            formVerification,
            codeObsolete,
            setCodeObsolete,
            sendVerificationCode,
            fillRegistrationForm,
        } = this.props.registrationStore

        const {
            verifyCode,
            originalPhone,
            formAttributes,
            canResend,
            codeCheckedTimes
        } = this.state

        const phones: CountryCodeISO[] = form?.phones
            .sort((a, b) => a.order - b.order)
            .map(({ id }) => id as CountryCodeISO)

        const phoneField = form?.fields?.find(item => item.id === 'phone')

        const phoneChanged = formAttributes.phone !== originalPhone
        const showResendButton = phoneChanged && canResend

        const codeLength = formVerification.verificationField.length
        const ttl = formVerification.verificationField.ttl

        return (
            <Grid container spacing={4}>
                {sortBy(form?.fields, 'order')?.map(field => {
                    // Телефон будет последним в списке
                    if (field.id === 'phone') return null

                    return (
                        <Grid key={field.id} item xs={12}>
                            {this.getReadOnlyField(field)}
                        </Grid>
                    )
                }).filter(Boolean)}

                <Grid item xs={12}>
                    <p id={'fieldLabel-phone'} className={styles.label}>
                        {phoneField.text}
                        {phoneField.required ? (
                            <span className={styles.required}>{' (обязательно)'}</span>
                        ) : ''}
                    </p>

                    <IntlPhoneInput
                        countries={phones}
                        value={formAttributes.phone}
                        forceCountry={this.state.phoneCountryCode}
                        onValueChange={newValue => {
                            this.setState(prevState => ({
                                formAttributes: {
                                    ...prevState.formAttributes,
                                    phone: newValue,
                                },
                                isFormValid: true,
                                verifyCode: '',
                                codeCheckedTimes: 0
                            }))
                        }}
                        disabled={!canResend}
                        error={false}
                        errorText={this.getFieldErrorById('phone')}
                    />
                </Grid>

                {showResendButton && (
                    <Grid item xs={12} style={{ display: 'flex', justifyContent: 'center' }}>
                        <Button
                            id="resendCompletedFormBtn"
                            color="primary"
                            style={{ textTransform: 'none' }}
                            onClick={() => {
                                this.setState({
                                    canResend: false,
                                })
                                fillRegistrationForm({ fields: this.formFields }, () => false)
                                setCodeObsolete(false)
                            }}
                            disabled={isNil(formAttributes.phone) || !this.isFieldsValid()}
                        >
                            {'Отправить код'}
                        </Button>
                    </Grid>
                )}

                <Grid item xs={12}>
                    <div id="verificationFieldLabel" className={styles.verifyText}>
                        <span>{'Для завершения регистрации введите код подтверждения '}</span>
                        <Timer
                            key={String(canResend)}
                            secondsLeft={ttl}
                            countdownCallback={this.verificationTimerCallback}
                            showBrackets
                        />
                    </div>
                    {!showResendButton && (
                        <TextField
                            id="verifyCodeInput"
                            placeholder={'Код'}
                            className={styles.textField}
                            value={verifyCode}
                            maxLength={codeLength}
                            endAdornment={<div/>}
                            disabled={codeObsolete}
                            onValueChange={value => {
                                if (onlyDigits.test(value)) {
                                    this.setState({ verifyCode: value })
                                }
                            }}
                        />
                    )}
                </Grid>

                <Grid item xs={12}>
                    <Button
                        className={styles.submitButton}
                        fullWidth
                        id="verifyButton"
                        color="primary"
                        variant="contained"
                        disabled={codeCheckedTimes >= MAX_PHONE_CHECKS || !verifyCode || !this.isFieldsValid()
                            || showResendButton || verifyCode.length !== codeLength || codeObsolete}
                        onClick={() => {
                            this.setState(prevState => ({
                                codeCheckedTimes: prevState.codeCheckedTimes + 1
                            }))
                            sendVerificationCode(verifyCode)
                        }}
                    >
                        {'Готово'}
                    </Button>
                </Grid>
            </Grid>
        )
    }

    getTemplatePersonalLink = (): JSX.Element => {
        const {
            form,
        } = this.props.registrationStore

        if (!form.personalLink) return null

        const escapeRegExp = (expression: string) => {
            return expression.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        }

        let text = form.personalLink?.template

        form.personalLink?.links.forEach(({link, label, id}) => {
            const regStr = `{${id}}`
            const replacementRegexp = new RegExp(escapeRegExp(regStr), 'g')
            text = text.replace(replacementRegexp, `<a href="${link}" target='_blank' rel='noopener noreferrer'>${label}</a>`)
        })

        text += '<span style="color: #EB5757"> (обязательно)</span>'

        return (
            <div style={{ whiteSpace: 'pre-wrap' }} dangerouslySetInnerHTML={{ __html: text }} />
        )
    }

    getOldTemplatePersonalLink = (personalDataLink: string): JSX.Element => {
        return (
            <>
                <br />
                <span>
                    {'Я согласен с обработкой персональных данных.'}
                </span>
                <br />
                {personalDataLink && (
                    <a
                        href={personalDataLink}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Политика конфиденциальности
                    </a>
                )}
                {!personalDataLink && (
                    <span>{'Политика конфиденциальности'}</span>
                )}
                <span className={styles.required}>
                    {' (обязательно)'}
                </span>
            </>
        )
    }

    render() {
        const {
            clientId,
            form,
            fillRegistrationForm,
            formVerification,
            editError,
            personalDataLink,
            setCaptchaToken,
        } = this.props.registrationStore

        const { agreement, isFormValid, formAttributes } = this.state

        const phones: CountryCodeISO[] = form?.phones
            .sort((a, b) => a.order - b.order)
            .map(({ id }) => id as CountryCodeISO)

        // Во время верификации поля более нельзя редактировать
        if (formVerification?.verificationField?.id === 'phone') {
            return this.getVerificationPage()
        }

        return (
            <Grid container spacing={6}>
                {sortBy(form?.fields, 'order')?.map(field => {
                    return (<React.Fragment key={field.id}><Grid item xs={12}>
                        {this.getFormFieldById(field, phones)}
                    </Grid>
                    {editError[field.id] && (
                        <Grid item xs={12} className={styles.editError}>
                            <p id="editErrorMessage">{editError[field.id]}</p>
                        </Grid>
                    )}
                    </React.Fragment>)
                })}

                <Grid item xs={12}>
                    <SetCaptcha
                        clientId={clientId}
                        onChange={answer => this.setState({ captcha: answer.length > 0 })}
                        onTokenChange={token => setCaptchaToken(token)}
                    />
                </Grid>

                <Grid item xs={12}>
                    <Checkbox
                        id="agreementCheckbox"
                        label={(
                            <>
                                {form?.personalLink
                                    ? this.getTemplatePersonalLink()
                                    : this.getOldTemplatePersonalLink(personalDataLink)
                                }
                            </>
                        ) as any}
                        checked={agreement}
                        onValueChange={newValue => this.setState({ agreement: newValue })}
                    />
                </Grid>

                <Grid item xs={12}>
                    {!isFormValid && (
                        <p id="invalidFormErrorText" className={styles.error}>Форма заполнена некорректно</p>
                    )}
                    <Button
                        className={styles.submitButton}
                        fullWidth
                        id="sendButton"
                        color="primary"
                        variant="contained"
                        style={{
                            background: `linear-gradient(90deg, rgba(47,128,237,1) 0%, rgba(47,128,237,1) ${
                                this.filledPrecentage
                            }%, rgba(218,220,223,1) ${this.filledPrecentage === 0 ? 0 : this.filledPrecentage + 2}%)`,
                            transition: `all 2s linear`
                        }}
                        disabled={!this.isAllRequiredFieldsFilled || this.isAllFieldsEmpty}
                        onClick={async () => {
                            if (!this.isFieldsValid()) {
                                this.setState({ isFormValid: false })
                                return
                            }
                            this.setState({
                                originalPhone: formAttributes.phone
                            })
                            fillRegistrationForm(
                                { fields: this.formFields },
                                (result: components["schemas"]["Application"]) => {
                                    if (result?.verificationField?.id === 'phone') {
                                        const phoneField = form?.fields?.find(item => item.id === 'phone')
                                        const phoneFieldFilled = Boolean(formAttributes.phone)

                                        // Если поле обязательное и пришло поле для верификации - показываем
                                        // Если поле не обязательное и мы его не заполнили - не показываем, даже если пришло с бэка про верификацию
                                        if (phoneField?.required || phoneFieldFilled) return false
                                    }
                                    return true
                                }
                            )
                        }}
                    >
                        {'Отправить'}
                    </Button>
                </Grid>
            </Grid>
        )
    }
}
